import React, { useEffect, useState } from 'react';
import { ShieldHalf, UserCheck, MonitorSmartphone, Network, AppWindow, Database, Loader2, Landmark } from 'lucide-react';
import { ApiNames } from '../../components/Common/ApiManagement/apiServices';

const ZERO_TRUST_AREAS = [
	{
		key: 'Identity',
		label: 'Identity',
		icon: UserCheck,
		color: 'from-cyan-500 to-cyan-300',
		iconColor: 'text-cyan-500',
	},
	{
		key: 'Devices',
		label: 'Devices',
		icon: MonitorSmartphone,
		color: 'from-amber-500 to-amber-300',
		iconColor: 'text-amber-500',
	},
	{
		key: 'Networks',
		label: 'Networks',
		icon: Network,
		color: 'from-emerald-400 to-emerald-200',
		iconColor: 'text-emerald-400',
	},
	{
		key: 'Applications',
		label: 'Applications',
		icon: AppWindow,
		color: 'from-cyan-500 to-cyan-300',
		iconColor: 'text-cyan-500',
	},
	{
		key: 'Data',
		label: 'Data',
		icon: Database,
		color: 'from-red-500 to-red-300',
		iconColor: 'text-red-500',
	},
];

function clamp(val, min, max) {
	return Math.max(min, Math.min(max, val));
}

const ZeroTrustDial = ({ label, value, description, icon, color, iconColor }) => {
	// value: 0-100
	const radius = 56;
	const stroke = 8;
	const normalizedRadius = radius - stroke / 2;
	const circumference = 2 * Math.PI * normalizedRadius;
	const percent = clamp(value, 0, 100);
	const offset = circumference - (percent / 100) * circumference;
	const Icon = icon;
	return (
		<div className="flex flex-col items-center w-full">
			<div className="relative w-28 h-28 flex items-center justify-center mb-2">
				<svg width={radius * 2} height={radius * 2} className={`risk-dial ${color}`}>
					<circle
						className="dial-ring"
						stroke="#e5e7eb"
						fill="none"
						strokeWidth={stroke}
						cx={radius}
						cy={radius}
						r={normalizedRadius}
					/>
					<circle
						className="dial-ring"
						strokeDasharray={circumference}
						strokeDashoffset={offset}
						strokeLinecap="round"
						stroke={`url(#${label.replace(/\s+/g, '-')}-gradient)`}
						fill="none"
						strokeWidth={stroke}
						cx={radius}
						cy={radius}
						r={normalizedRadius}
						style={{ transition: 'stroke-dashoffset 1s cubic-bezier(.4,2,.6,1)' }}
					/>
					<defs>
						<linearGradient id={`${label.replace(/\s+/g, '-')}-gradient`} x1="0" y1="0" x2="1" y2="1">
							<stop offset="0%" stopColor="var(--tw-gradient-from)" />
							<stop offset="100%" stopColor="var(--tw-gradient-to)" />
						</linearGradient>
					</defs>
				</svg>
				<span className="absolute inset-0 flex items-center justify-center text-xl font-bold text-gray-800 dark:text-white">
					{percent}%
				</span>
			</div>
			<span className="flex items-center text-xs text-black-400 font-bold mb-1">
				<Icon className={`w-4 h-4 mr-1 ${iconColor}`} />{label}
			</span>
			<span className="text-xs text-gray-500 text-center min-h-[32px]">{description}</span>
		</div>
	);
};

const ZeroTrustTab = ({ scanId }) => {
	const [zeroTrustData, setZeroTrustData] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [cached, setCached] = useState(false);
	const [lastUpdated, setLastUpdated] = useState(null);
	const [tokensUsed, setTokensUsed] = useState(null);
	const [costUsd, setCostUsd] = useState(null);

	useEffect(() => {
		const fetchCachedZeroTrust = async () => {
			setLoading(true);
			setError(null);
			setCached(false);
			try {
				const cachedResp = await fetch(ApiNames.AI_HUB_FETCH_CACHED_AI_DATA(scanId, "Zero Trust"), { method: 'POST', credentials: 'include' });
				if (cachedResp.status === 200) {
					const cachedJson = await cachedResp.json();
					if (cachedJson && cachedJson.response) {
						const parsed = JSON.parse(cachedJson.response);
						setZeroTrustData(parsed);
						setCached(true);
						setLastUpdated(cachedJson.lastUpdated || null);
						setTokensUsed(cachedJson.tokens_used || null);
						setCostUsd(cachedJson.cost_usd || null);
						setLoading(false);
						return;
					}
				}
				setZeroTrustData(null);
				setCached(false);
			} catch (err) {
				setZeroTrustData(null);
				setCached(false);
			} finally {
				setLoading(false);
			}
		};
		fetchCachedZeroTrust();
	}, [scanId]);

	const handleGenerate = async () => {
		setLoading(true);
		setError(null);
		setCached(false);
		try {
			const response = await fetch(ApiNames.AI_HUB_ZERO_TRUST(scanId), { method: 'POST', credentials: 'include' });
			if (!response.ok) throw new Error('Failed to generate Zero Trust analysis.');
			const json = await response.json();
			const parsed = JSON.parse(json.response);
			setZeroTrustData(parsed);
			setTokensUsed(json.tokens_used || null);
			setCostUsd(json.cost_usd || null);
			setLastUpdated(json.lastUpdated || null);
			setCached(false);
		} catch (err) {
			setError('Unable to generate Zero Trust analysis. Please try again.');
			setZeroTrustData(null);
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="flex flex-col items-center gap-8">
			<div className="w-full max-w-6xl">
				<div className="card-header flex items-center justify-between mb-2">
					<div className="card-title flex items-center gap-2">
						<ShieldHalf className="w-6 h-6 text-blue-400" />
						<h3 className="font-bold text-lg">AI Zero Trust Alignment - 5 Pillars</h3>
					</div>
					<span className="card-badge flex items-center gap-1 bg-green-100 text-green-600 px-3 py-1 rounded-full text-xs font-semibold">
						<Landmark className="w-4 h-4 mr-1" />EO 14028 Mapped
					</span>
				</div>
				<p className="text-gray-400 mb-6 text-sm mt-2">
					AI maps your current controls to Executive Order 14028 and the Zero Trust Maturity Model pillars, identifying gaps in your Zero Trust journey.
				</p>
				<div className="w-full flex flex-col items-center">
					{!cached && !zeroTrustData && !loading && !error && (
						<div className="flex flex-col items-center py-12">
							<button
								className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg shadow transition-all duration-150"
								onClick={handleGenerate}
								disabled={loading || !scanId}
							>
								Generate Zero Trust Analysis
							</button>
						</div>
					)}
					{loading && (
						<div className="ai-response flex flex-col items-center justify-center py-12 animate-fadeIn">
							<Loader2 className="animate-spin text-blue-400 w-10 h-10 mb-4" />
							<span className="text-gray-400">Generating Zero Trust analysis...</span>
						</div>
					)}
					{error && !loading && (
						<div className="ai-response text-center text-red-400 py-12">{error}</div>
					)}
					{zeroTrustData && !loading && !error && (
						<>
							<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 mb-6 w-full">
								{ZERO_TRUST_AREAS.map(area => (
									<div key={area.key} className="bg-base-100 rounded-xl shadow-xl p-6 flex flex-col items-center animate-fadeIn">
										<ZeroTrustDial
											label={area.label}
											value={zeroTrustData[area.key]?.percentage ?? 0}
											description={zeroTrustData[area.key]?.description || ''}
											icon={area.icon}
											color={area.color}
											iconColor={area.iconColor}
										/>
									</div>
								))}
							</div>
							<div className="ai-response bg-base-100 rounded-xl shadow-xl p-6 animate-fadeIn mb-4 w-full">
								<h4 className="font-bold text-lg mb-2">Zero Trust Gap Analysis</h4>
								<p className="mb-4 text-black-400" style={{whiteSpace: 'pre-line'}}>{zeroTrustData.analysis}</p>
								{tokensUsed && <p className="mb-2 text-gray-400">Tokens used: {tokensUsed}. Cost: ${costUsd}.</p>}
								{cached && lastUpdated && (
									<p className="mb-2 text-gray-400">Viewing cached Zero Trust analysis. Last updated: {lastUpdated}.</p>
								)}
								<button
									className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg shadow transition-all duration-150"
									onClick={handleGenerate}
									disabled={loading || !scanId}
								>
									Regenerate Zero Trust Analysis
								</button>
							</div>
						</>
					)}
				</div>
			</div>
		</div>
	);
};

export default ZeroTrustTab;
